import { usePageMeta } from "@/lib/seo";
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, ClipboardPaste, Copy, ImagePlus, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { QuotaBlock, UsagePill } from "@/components/UsagePill";
import { useFavorite } from "@/components/dashboard/ReplyCard";
import { LiveCamera } from "@/components/scan/LiveCamera";
import { InstallAppButton } from "@/components/scan/InstallAppButton";
import { api, errMsg, newRequestId } from "@/lib/api";

const STYLES = [
  { id: "flirty", name: "Flirty" }, { id: "smooth", name: "Smooth Rizz" }, { id: "funny", name: "Funny" },
  { id: "cute", name: "Cute" }, { id: "confident", name: "Confident" },
];
const STYLE_KEY = "rizzora.quickStyle";
const SHARE_CACHE = "rizzora-share";

const QuickReply = ({ reply, index }) => {
  const n = index + 1;
  const [copied, setCopied] = useState(false);
  const [favId, favBusy, toggleFav] = useFavorite(reply);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reply.text);
      setCopied(true);
      toast.success("Copied! Paste it in your chat.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.message("Clipboard blocked — select the text and copy it manually.");
    }
  };
  return (
    <div data-testid={`scan-reply-${n}`} className="panel p-4 rise flex gap-3 items-start" style={{ animationDelay: `${index * 0.06}s` }}>
      <div className="flex-1 min-w-0">
        <span className="text-[11px] uppercase tracking-widest font-bold text-accent">{reply.label}</span>
        <p data-testid={`scan-reply-text-${n}`} translate="no" className="notranslate mt-1 text-base leading-snug font-medium select-text">{reply.text}</p>
      </div>
      <div className="flex flex-col gap-2">
        <button data-testid={`scan-reply-copy-${n}`} onClick={copy} aria-label="Copy reply" className="h-10 px-4 rounded-full brand-gradient text-white text-xs font-semibold inline-flex items-center gap-1.5">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? "Copied" : "Copy"}
        </button>
        <button data-testid={`scan-reply-favorite-${n}`} onClick={toggleFav} disabled={favBusy} aria-pressed={!!favId} className="h-8 rounded-full text-xs border border-border hover:border-primary/50">
          {favId ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );
};

const SourceButton = ({ icon: Icon, label, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} className="panel p-4 flex flex-col items-center gap-2 hover:border-primary/60 transition-colors">
    <span className="h-11 w-11 rounded-full brand-gradient text-white flex items-center justify-center"><Icon className="h-5 w-5" /></span>
    <span className="text-xs font-semibold text-center">{label}</span>
  </button>
);

export default function Scan() {
  usePageMeta({ title: "Scan a chat" });
  const [style, setStyle] = useState(() => localStorage.getItem(STYLE_KEY) || "flirty");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(false);
  const [usage, setUsage] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/usage").then((r) => setUsage(r.data)).catch(() => {}); }, []);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const run = useCallback(async (blob, st) => {
    setBusy(true); setError(""); setQuota(false);
    const form = new FormData();
    form.append("file", blob, blob.name || "scan.jpg");
    form.append("style", st);
    form.append("request_id", newRequestId());
    try {
      const { data } = await api.post("/quick-replies", form, { timeout: 60000 });
      setResult(data);
      if (data.usage) setUsage(data.usage);
    } catch (e) {
      setQuota(e?.response?.status === 402);
      setError(errMsg(e, "Couldn't create replies. Please retry."));
    } finally {
      setBusy(false);
    }
  }, []);

  const takeImage = useCallback((blob) => {
    if (!blob?.type?.startsWith("image/")) return toast.error("That isn't an image.");
    if (blob.size > 10 * 1024 * 1024) return toast.error("Image must be under 10 MB.");
    setImage(blob); setPreview(URL.createObjectURL(blob)); setResult(null); setCamera(false);
    run(blob, localStorage.getItem(STYLE_KEY) || "flirty");
  }, [run]);

  useEffect(() => {
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith("image/"));
      if (item) { e.preventDefault(); takeImage(item.getAsFile()); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [takeImage]);

  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("shared") || !("caches" in window)) return;
    (async () => {
      const cache = await caches.open(SHARE_CACHE);
      const res = await cache.match("/shared-image");
      if (res) { await cache.delete("/shared-image"); takeImage(await res.blob()); }
      window.history.replaceState(null, "", "/scan");
    })().catch(() => {});
  }, [takeImage]);

  const pasteFromClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const type = it.types.find((t) => t.startsWith("image/"));
        if (type) return takeImage(await it.getType(type));
      }
      toast.error("No screenshot in your clipboard. Copy one first, then tap Paste.");
    } catch {
      toast.message("Your browser blocked paste — use “Pick screenshot” instead, or press Ctrl/⌘+V.");
    }
  };

  const pickStyle = (id) => {
    setStyle(id);
    localStorage.setItem(STYLE_KEY, id);
    if (image && !busy) run(image, id);
  };

  const reset = () => { setImage(null); setPreview(""); setResult(null); setError(""); };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-xl mx-auto px-4 py-8 space-y-5" data-testid="scan-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Scan a chat</h1>
            <p className="text-sm text-muted-foreground mt-1">Snap your laptop screen, paste a screenshot or share one in — get 3 replies to copy.</p>
          </div>
          <InstallAppButton />
        </div>
        <UsagePill usage={usage} />

        <div className="flex flex-wrap gap-2" data-testid="scan-style-options">
          {STYLES.map((s) => (
            <button key={s.id} data-testid={`scan-style-${s.id}`} onClick={() => pickStyle(s.id)} aria-pressed={style === s.id}
              className={`h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${style === s.id ? "brand-gradient text-white border-transparent" : "border-border hover:border-primary/50"}`}>
              {s.name}
            </button>
          ))}
        </div>

        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="scan-file-input" onChange={(e) => { takeImage(e.target.files?.[0]); e.target.value = ""; }} />
        {camera && <LiveCamera onCapture={takeImage} onClose={() => setCamera(false)} />}
        {!camera && !image && (
          <div className="grid grid-cols-3 gap-3">
            <SourceButton testId="scan-camera-button" icon={Camera} label="Live camera" onClick={() => setCamera(true)} />
            <SourceButton testId="scan-paste-button" icon={ClipboardPaste} label="Paste screenshot" onClick={pasteFromClipboard} />
            <SourceButton testId="scan-pick-button" icon={ImagePlus} label="Pick screenshot" onClick={() => fileRef.current?.click()} />
          </div>
        )}
        {image && (
          <div className="relative">
            <img src={preview} alt="Scanned chat" data-testid="scan-preview" className="w-full max-h-64 object-contain rounded-2xl bg-secondary/40" />
            <button data-testid="scan-reset-button" onClick={reset} aria-label="Scan another" className="absolute top-2 right-2 h-9 w-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center"><X className="h-4 w-4" /></button>
          </div>
        )}

        <section className="space-y-3" data-testid="scan-results">
          {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="scan-loading"><Loader2 className="h-4 w-4 animate-spin text-primary" />Reading the chat…</div>}
          {busy && [0, 1, 2].map((i) => <div key={i} className="h-20 rounded-3xl shimmer" />)}
          {!busy && error && (quota ? <QuotaBlock message={error} /> : (
            <div className="panel p-4 text-sm space-y-3" data-testid="scan-error">
              <p>{error}</p>
              {image && <button data-testid="scan-retry-button" onClick={() => run(image, style)} className="text-primary font-semibold inline-flex items-center gap-1"><RefreshCw className="h-4 w-4" />Retry</button>}
            </div>
          ))}
          {!busy && result && (
            <>
              {result.last_message && <p className="text-xs text-muted-foreground" data-testid="scan-last-message">Replying to: <span translate="no" className="notranslate italic">“{result.last_message}”</span></p>}
              {result.replies.map((r, i) => <QuickReply key={`${r.text}-${i}`} reply={r} index={i} />)}
              <button data-testid="scan-new-replies-button" onClick={() => run(image, style)} className="text-sm font-semibold text-primary inline-flex items-center gap-1.5"><RefreshCw className="h-4 w-4" />New replies</button>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
